import type { ColDef } from "ag-grid-community";
import type {
    IAGAmtsDataRow,
    IAGPosQtysDataRow,
} from "./AlgoTable.types";
import { getColDefs } from "@/components/AGColDefs/Blotter";
import {
    numericFormatterNoPrecision,
    currencyFormatter,
} from "@/utils/helper_functions";
import {
    ASSET_MARGIN_REPORT,
    BALANCE_UPDATE,
    ORDER_EVENT,
    POSITION_MARGIN_REPORT,
    POSITION_UPDATE,
    TRADE_EVENT,
    TRANSFER_EVENT,
} from "../../services/consts";
import type { BlotterFeedType } from "../../services/blotterApi";

/** Column definition keys per feed type. */
export const COL_DEF_KEYS_BY_FEED: Record<BlotterFeedType, string[]> = {
    [ORDER_EVENT]: [
        "transact_time", "status", "symbol", "price", "order_qty", "cum_qty",
        "leaves_qty", "side", "venue", "client_order_id", "exchange_misc_fee_amt",
        "exchange_misc_fee_curr", "tax_misc_fee_amt", "tax_misc_fee_curr",
        "local_comm_misc_fee_amt", "local_comm_misc_fee_curr", "ord_type", "exec_type",
        "placement_strategy", "avg_px", "order_id", "exec_id", "contract_size",
    ],
    [POSITION_UPDATE]: [
        "detail_expander", "updated", "symbol", "total_long", "total_short",
        "parties", "contract_size", "pos_event_type", "executing_firm", "exchange",
        "customer_account", "intermediary", "account_wallet", "blockchain_wallet",
        "pos_qtys", "clearing_date",
    ],
    [BALANCE_UPDATE]: [
        "detail_expander", "updated", "pos_event_type ", "amts", "executing_firm",
        "exchange", "customer_account", "intermediary", "account_wallet",
        "blockchain_wallet", "clearing_date",
    ],
    [ASSET_MARGIN_REPORT]: [
        "transact_time", "asset", "rpt_type", "asset_margin_ratio", "initial_margin",
        "margin_balance", "unrealized_pnl", "available_balance",
        "executing_firm", "exchange", "customer_account", "intermediary",
        "account_wallet", "blockchain_wallet", "maintenance_margin",
        "updated", "current_date",
    ],
    [POSITION_MARGIN_REPORT]: [
        "transact_time", "updated", "symbol", "initial_margin", "margin_balance",
        "unrealized_pnl", "available_balance", "maintenance_margin",
        "total_initial_margin", "total_maintenance_margin", "total_unrealized_pnl",
        "total_margin_balance", "total_margin_ratio", "margins", "executing_firm",
        "exchange", "customer_account", "intermediary", "account_wallet",
        "blockchain_wallet", "current_date", "rpt_type",
    ],
    [TRADE_EVENT]: [
        "transact_time", "symbol", "side", "last_px", "last_qty", "order_id",
        "trade_id", "venue", "exec_type", "contract_size",
    ],
    [TRANSFER_EVENT]: [
        "updated", "asset", "transfer_type", "txid", "quantity", "transfer_status",
        "src_executing_firm", "src_exchange", "src_customer_account", "src_intermediary",
        "src_account_wallet", "src_blockchain_wallet", "dest_executing_firm",
        "dest_exchange", "dest_customer_account", "dest_intermediary",
        "dest_account_wallet", "dest_blockchain_wallet", "id", "details", "venue",
        "clearing_date",
    ],
};

/** Feeds that use master/detail (expandable rows). */
export const DETAIL_FEEDS: Set<BlotterFeedType> = new Set([
    POSITION_UPDATE,
    BALANCE_UPDATE,
]);

/** Detail grid column defs for position_update (pos_qtys). */
export const POSITION_UPDATE_DETAIL_COL_DEFS: ColDef<IAGPosQtysDataRow>[] = [
    { headerName: "Position Type", field: "pos_type", flex: 1 },
    { headerName: "Long Quantity", field: "long_qty", valueFormatter: numericFormatterNoPrecision, flex: 1 },
    { headerName: "Short Quantity", field: "short_qty", valueFormatter: numericFormatterNoPrecision, flex: 1 },
    { headerName: "Unrealized P&L", field: "unrealized_pnl", valueFormatter: (p: any) => currencyFormatter(p, "$"), flex: 1 },
];

/** Detail grid column defs for balance_update (amts). */
export const BALANCE_UPDATE_DETAIL_COL_DEFS: ColDef<IAGAmtsDataRow>[] = [
    { headerName: "Asset", field: "asset", flex: 1 },
    { headerName: "Amount Total", field: "amt_total", valueFormatter: numericFormatterNoPrecision, flex: 1 },
];

export function getColDefsForFeed(feedType: BlotterFeedType): ColDef[] {
    return getColDefs(COL_DEF_KEYS_BY_FEED[feedType]);
}